import { useState, useCallback, useEffect, useRef } from "react";
import { IconSparkles } from "@tabler/icons-react";
import { Loader2, Check, Circle, RotateCcw, AlertCircle } from "lucide-react";
import {
  generateQuiz as generateQuizAPI,
  reportQuizResult,
  GeneratedQuiz,
  QuizType as ServiceQuizType,
  DifficultyLevel
} from "@/services/journalAIService";
import { MCQPreview } from "./quiz/MCQPreview";
import { TrueFalsePreview } from "./quiz/TrueFalsePreview";
import { FillBlankPreview } from "./quiz/FillBlankPreview";
import { MatchingPreview } from "./quiz/MatchingPreview";
import { SpotMistakePreview } from "./quiz/SpotMistakePreview";

type QuizState = 'prompt' | 'loading' | 'active';

// Quiz types available
const quizTypes = [
  { id: 'mcq', label: 'MCQ', shortLabel: 'MCQ' },
  { id: 'true_false', label: 'True/False', shortLabel: 'T/F' },
  { id: 'fill_blank', label: 'Fill Blanks', shortLabel: 'Fill' },
  { id: 'matching', label: 'Matching', shortLabel: 'Match' },
  { id: 'spot_mistake', label: 'Spot Error', shortLabel: 'Spot' },
] as const;

type QuizTypeId = typeof quizTypes[number]['id'];

const countOptions = [3, 5, 7, 10];
const difficultyOptions = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

export interface QuizProgress {
  total: number;
  completed: number;
  correct: number;
}

// Persistent quiz state that survives collapse/expand
export interface QuizBlockState {
  generating?: boolean;  // AI-triggered generation in progress
  prompt?: string;
  quizType?: string;
  questionCount?: number;
  difficulty?: string;
  generatedQuiz?: GeneratedQuiz;
  answers?: Record<number, any>;
  error?: string;  // Error message if generation failed
}

interface QuizBlockProps {
  journalId: string;
  initialState?: QuizBlockState;
  onStateChange?: (state: QuizBlockState) => void;
  onProgressChange?: (progress: QuizProgress) => void;
  onTitleChange?: (title: string) => void;
}

// Display names for quiz types
const QUIZ_TYPE_LABELS: Record<string, string> = {
  mcq: 'MCQ',
  true_false: 'True/False',
  fill_blank: 'Fill Blanks',
  matching: 'Matching',
  spot_mistake: 'Spot Error',
};

export const QuizBlock = ({ journalId, initialState, onStateChange, onProgressChange, onTitleChange }: QuizBlockProps) => {
  // Determine initial UI state based on initialState
  const getInitialUIState = (): QuizState => {
    if (initialState?.generatedQuiz) return 'active';
    if (initialState?.generating) return 'loading';  // AI-triggered generation
    return 'prompt';
  };

  const [state, setState] = useState<QuizState>(getInitialUIState);
  const [quizType, setQuizType] = useState<QuizTypeId>((initialState?.quizType as QuizTypeId) || 'mcq');
  const [prompt, setPrompt] = useState(initialState?.prompt || '');
  const [questionCount, setQuestionCount] = useState(initialState?.questionCount || 5);
  const [difficulty, setDifficulty] = useState(initialState?.difficulty || 'medium');
  const [generatedQuiz, setGeneratedQuiz] = useState<GeneratedQuiz | null>(initialState?.generatedQuiz || null);
  const [answers, setAnswers] = useState<Record<number, any>>(initialState?.answers || {});

  // Progress tracking
  const [progress, setProgress] = useState<QuizProgress>(() => {
    if (initialState?.generatedQuiz && initialState?.answers) {
      const total = initialState.generatedQuiz.items.length;
      const completed = Object.keys(initialState.answers).length;
      const correct = Object.values(initialState.answers).filter((a: any) => a.correct).length;
      return { total, completed, correct };
    }
    return { total: 0, completed: 0, correct: 0 };
  });

  // Sync state when parent updates (e.g., when generation completes)
  useEffect(() => {
    if (initialState?.generatedQuiz && !generatedQuiz) {
      // Generation completed externally - update our state
      setGeneratedQuiz(initialState.generatedQuiz);
      setAnswers(initialState.answers || {});
      setProgress({
        total: initialState.generatedQuiz.items.length,
        completed: Object.keys(initialState.answers || {}).length,
        correct: 0
      });
      setState('active');
    }

    // Also update if generating flag changed to false with error
    if (initialState?.generating === false && state === 'loading' && !initialState?.generatedQuiz) {
      setState('prompt');  // Go back to prompt on error
    }
  }, [initialState?.generatedQuiz, initialState?.generating, initialState?.answers, generatedQuiz, state]);

  // Emit state changes to parent
  const emitStateChange = useCallback((updates: Partial<QuizBlockState>) => {
    if (onStateChange) {
      const newState: QuizBlockState = {
        prompt,
        quizType,
        questionCount,
        difficulty,
        generatedQuiz: generatedQuiz || undefined,
        answers,
        ...updates,
      };
      onStateChange(newState);
    }
  }, [onStateChange, prompt, quizType, questionCount, difficulty, generatedQuiz, answers]);

  // Get placeholder based on quiz type
  const getPlaceholder = () => {
    switch (quizType) {
      case 'mcq':
        return "What topic? e.g., 'Photosynthesis process and its stages'";
      case 'true_false':
        return "What facts to test? e.g., 'Solar system planets and their properties'";
      case 'fill_blank':
        return "What concepts? e.g., 'Key biology terms and definitions'";
      case 'matching':
        return "What pairs? e.g., 'Match countries with their capitals'";
      case 'spot_mistake':
        return "What errors to spot? e.g., 'Common misconceptions in biology'";
      default:
        return "Describe what the quiz should cover...";
    }
  };

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!prompt.trim()) return;

    setState('loading');
    setError(null);

    try {
      const response = await generateQuizAPI({
        journal_id: journalId,
        topic: prompt.trim(),
        quiz_type: quizType as ServiceQuizType,
        question_count: questionCount,
        difficulty: difficulty as DifficultyLevel,
      });

      const quiz = response.quiz;
      setGeneratedQuiz(quiz);
      setAnswers({});
      setProgress({ total: quiz.items.length, completed: 0, correct: 0 });
      setState('active');

      // Update block title with quiz type and generated title
      // Format: "MCQ: Introduction to Neuroplasticity"
      const typeLabel = QUIZ_TYPE_LABELS[quizType] || quizType.toUpperCase();
      const blockTitle = quiz.title
        ? `${typeLabel}: ${quiz.title}`
        : `${typeLabel} Quiz`;
      onTitleChange?.(blockTitle);

      // Emit state change with new quiz
      emitStateChange({ generatedQuiz: quiz, answers: {} });
    } catch (err) {
      console.error('Failed to generate quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate quiz. Please try again.');
      setState('prompt');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Track if we've already reported this quiz completion
  const hasReportedRef = useRef(false);

  const handleProgressUpdate = useCallback((newProgress: QuizProgress) => {
    setProgress(newProgress);
    onProgressChange?.(newProgress);
  }, [onProgressChange]);

  const handleAnswersChange = useCallback((newAnswers: Record<number, any>) => {
    setAnswers(newAnswers);
    emitStateChange({ answers: newAnswers });
  }, [emitStateChange]);

  // Report quiz result when completed (all questions answered)
  useEffect(() => {
    const isCompleted = progress.completed === progress.total && progress.total > 0;

    // Only report once per quiz completion
    if (isCompleted && generatedQuiz && !hasReportedRef.current) {
      hasReportedRef.current = true;

      // Fire and forget - don't block UI
      reportQuizResult({
        journal_id: journalId,
        quiz_type: quizType as ServiceQuizType,
        topic: generatedQuiz.title || prompt,
        difficulty: difficulty as DifficultyLevel,
        total_questions: progress.total,
        correct_answers: progress.correct,
      }).then((result) => {
        console.log('[Quiz] Learning progress updated:', result.learning_progress);
      }).catch((err) => {
        // Non-critical - just log
        console.warn('[Quiz] Failed to report result:', err);
      });
    }

    // Reset the ref when quiz changes or retake
    if (progress.completed === 0 && progress.total > 0) {
      hasReportedRef.current = false;
    }
  }, [progress, generatedQuiz, journalId, quizType, difficulty, prompt]);

  const handleRetake = () => {
    setAnswers({});
    setProgress({ total: generatedQuiz?.items.length || 0, completed: 0, correct: 0 });
    emitStateChange({ answers: {} });
  };

  // Render the appropriate quiz component based on type
  const renderQuiz = () => {
    if (!generatedQuiz) return null;

    const commonProps = {
      initialAnswers: answers,
      onAnswersChange: handleAnswersChange,
      onProgressChange: handleProgressUpdate,
    };

    switch (generatedQuiz.type) {
      case 'mcq':
        return <MCQPreview items={generatedQuiz.items as any} {...commonProps} />;
      case 'true_false':
        return <TrueFalsePreview items={generatedQuiz.items as any} {...commonProps} />;
      case 'fill_blank':
        return <FillBlankPreview items={generatedQuiz.items as any} {...commonProps} />;
      case 'matching':
        return <MatchingPreview items={generatedQuiz.items as any} initialAnswers={answers as any} onAnswersChange={handleAnswersChange as any} onProgressChange={handleProgressUpdate} />;
      case 'spot_mistake':
        return <SpotMistakePreview items={generatedQuiz.items as any} {...commonProps} />;
      default:
        return <p className="text-sm text-slate-500">Unknown quiz type</p>;
    }
  };

  // Calculate status
  const isCompleted = progress.completed === progress.total && progress.total > 0;
  const isPartiallyDone = progress.completed > 0 && progress.completed < progress.total;
  const scorePercent = progress.total > 0 ? Math.round((progress.correct / progress.total) * 100) : 0;

  // Prompt State
  if (state === 'prompt') {
    return (
      <div className="pt-4 animate-fade-in-up">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden focus-within:border-slate-300 transition-colors">
          {/* Header: Quiz Type Selector - Segmented Pills */}
          <div className="px-4 pt-4 pb-2 border-b border-slate-100 overflow-x-auto">
            <div className="flex items-center gap-1.5 sm:gap-1 p-1 bg-slate-100 rounded-lg w-fit min-w-max">
              {quizTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setQuizType(type.id)}
                  className={`px-4 py-2.5 sm:px-3 sm:py-1.5 text-sm sm:text-xs font-medium rounded-md transition-all ${quizType === type.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main: Prompt Textarea */}
          <div className="px-4 py-3">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              className="w-full min-h-[80px] resize-none border-0 outline-none text-sm text-slate-700 placeholder:text-slate-400 bg-transparent"
            />
          </div>

          {/* Footer: Options Toolbar */}
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
              {/* Left: Question Count Pills */}
              <div className="flex items-center gap-2">
                <span className="text-sm sm:text-xs text-slate-400 font-medium whitespace-nowrap">Questions</span>
                <div className="flex gap-2 sm:gap-1">
                  {countOptions.map((count) => (
                    <button
                      key={count}
                      onClick={() => setQuestionCount(count)}
                      className={`min-w-[44px] min-h-[44px] sm:min-w-[28px] sm:min-h-[28px] sm:w-7 sm:h-7 text-sm sm:text-xs font-medium rounded-md transition-all ${questionCount === count
                        ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-white text-slate-500 hover:bg-slate-100 ring-1 ring-slate-200'
                        }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              {/* Center: Difficulty Pills */}
              <div className="flex items-center gap-2">
                <span className="text-sm sm:text-xs text-slate-400 font-medium">Level</span>
                <div className="flex gap-2 sm:gap-1">
                  {difficultyOptions.map((diff) => (
                    <button
                      key={diff.value}
                      onClick={() => setDifficulty(diff.value)}
                      className={`px-4 py-2.5 sm:px-2.5 sm:py-1 text-sm sm:text-xs font-medium rounded-md transition-all ${difficulty === diff.value
                        ? diff.value === 'easy'
                          ? 'bg-green-100 text-green-700 ring-1 ring-green-200'
                          : diff.value === 'medium'
                            ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
                            : 'bg-red-100 text-red-700 ring-1 ring-red-200'
                        : 'bg-white text-slate-500 hover:bg-slate-100 ring-1 ring-slate-200'
                        }`}
                    >
                      {diff.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Generate Button */}
              <button
                onClick={handleSubmit}
                disabled={!prompt.trim()}
                className={`flex items-center justify-center gap-1.5 px-4 py-3 sm:py-2 rounded-lg font-medium text-sm transition-all w-full sm:w-auto ${prompt.trim()
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                  : 'bg-slate-100 text-slate-400'
                  }`}
              >
                <IconSparkles className="w-4 h-4" />
                Generate
              </button>
            </div>
          </div>
        </div>
        {error && (
          <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 border border-red-100 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2 text-center">
          <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">⌘</kbd> + <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Enter</kbd> to generate
        </p>
      </div>
    );
  }

  // Loading State
  if (state === 'loading') {
    return (
      <div className="py-8 animate-scale-fade-in">
        <div className="flex flex-col items-center justify-center gap-4 p-8 rounded-xl ai-loading-shimmer border border-emerald-100">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-slate-700">
              Generating {questionCount} {quizTypes.find(t => t.id === quizType)?.label} questions...
            </p>
            <p className="text-xs text-slate-400">
              Difficulty: {difficultyOptions.find(d => d.value === difficulty)?.label}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active Quiz State
  if (state === 'active' && generatedQuiz) {
    return (
      <div className="pt-2 space-y-3 animate-content-fade-in">
        {/* Header with Status & Progress */}
        <div className="flex items-center justify-between">
          {/* Left: Status with progress icons */}
          <div className="flex items-center gap-2">
            {/* Progress dots */}
            <div className="flex items-center gap-1">
              {Array.from({ length: progress.total }).map((_, idx) => {
                const isAnswered = idx < progress.completed;
                return (
                  <div key={idx} className="relative">
                    {isAnswered ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 text-slate-300" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Status text */}
            {isCompleted ? (
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                Completed!
              </span>
            ) : isPartiallyDone ? (
              <span className="text-xs text-slate-400">
                {progress.completed}/{progress.total} answered
              </span>
            ) : (
              <span className="text-xs text-slate-400">
                {progress.total} questions
              </span>
            )}
          </div>

          {/* Right: Type & Difficulty badges */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              {quizTypes.find(t => t.id === quizType)?.label}
            </span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${difficulty === 'easy' ? 'bg-green-50 text-green-600' :
              difficulty === 'medium' ? 'bg-amber-50 text-amber-600' :
                'bg-red-50 text-red-600'
              }`}>
              {difficultyOptions.find(d => d.value === difficulty)?.label}
            </span>
          </div>
        </div>

        {/* Quiz Content */}
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          <div className="px-4">
            {renderQuiz()}
          </div>
        </div>

        {/* Footer: Results & Retake */}
        {(isCompleted || isPartiallyDone) && (
          <div className="flex items-center justify-between pt-2">
            {/* Results */}
            {isCompleted && (
              <div className="flex items-center gap-3">
                <div className={`text-sm font-medium ${scorePercent >= 80 ? 'text-emerald-600' :
                  scorePercent >= 50 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                  Score: {progress.correct}/{progress.total} ({scorePercent}%)
                </div>
                {scorePercent === 100 && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                    Perfect! 🎉
                  </span>
                )}
              </div>
            )}
            {isPartiallyDone && !isCompleted && (
              <div className="text-sm text-slate-500">
                {progress.correct} correct so far
              </div>
            )}

            {/* Retake/Start Over Button */}
            <button
              onClick={handleRetake}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {isCompleted ? 'Retake Quiz' : 'Start Over'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
};
